import { useCallback, useRef } from "react";
import { buildOutboundHandoffUrl, readAttributionContext } from "@nextday/attribution";
import { AUTH_HANDOFF_PARAM, AUTH_HANDOFF_VALUE } from "@src/funnel/handoffParams";
import {
  CLERK_SIGNIN_TOKEN_ANSWER_KEY,
  CLERK_EXTERNAL_SIGNIN_URL_KEY,
} from "@src/funnel/clerkConstants";
import type { ApplicationContextState } from "@src/funnel/useApplicationContext";
import type { StepType } from "@nextday/funnel-engine";
import { flushEventQueueSync } from "@src/funnel/hooks/useFunnelAnalytics";

const DEFAULT_PAYWALL_URL = "https://app.spicychat.ai/";

/** Step types that create an auth session the brand app can consume via `au_h=1`. */
const AUTH_STEP_TYPES: ReadonlySet<StepType> = new Set(["account_create", "user_sync"]);

type StepRef = { readonly type: string };

const hasAuthStep = (steps: ReadonlyArray<StepRef>): boolean =>
  steps.some((s) => AUTH_STEP_TYPES.has(s.type as StepType));

/**
 * Append the auth-handoff signal (`au_h=1`) to the outbound URL. The brand
 * app reads this on mount and fires `login({ prompt: "none" })`, which
 * silently exchanges the shared Kinde session cookie at the brand's auth
 * domain for brand-app tokens. See docs/PLAN_SATELLITE_AUTH.md §6.
 *
 * Idempotent: if the URL already carries `au_h` for any reason we leave it
 * alone (preserves any future per-brand override).
 */
const appendAuthHandoffSignal = (url: string): string => {
  const parsed = new URL(url);
  if (!parsed.searchParams.has(AUTH_HANDOFF_PARAM)) {
    parsed.searchParams.set(AUTH_HANDOFF_PARAM, AUTH_HANDOFF_VALUE);
  }
  return parsed.toString();
};

/**
 * Append `?ref=<affiliateId>` from the persisted attribution envelope so
 * Tapfiliate's `tap("detect")` on the brand app can attribute the visitor.
 *
 * `buildHandoffUrl` deliberately excludes affiliate IDs from forwarded params
 * (they should not leak to third-party ad pixels). However the destination
 * here is our own brand app which needs `?ref=` for Tapfiliate conversion
 * tracking, so we append it at the funnel-app layer.
 *
 * Idempotent: skips when no affiliateId is persisted or the URL already
 * carries `ref`.
 */
const appendAffiliateRef = (url: string): string => {
  try {
    const context = readAttributionContext();
    const affiliateId = context?.partners?.affiliateId;
    if (affiliateId === undefined || affiliateId === "") return url;

    const parsed = new URL(url);
    if (!parsed.searchParams.has("ref")) {
      parsed.searchParams.set("ref", affiliateId);
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * Forward `platformDetails.extraParams` from the attribution envelope as
 * top-level query params on the handoff URL. These are network-specific
 * tracking params (e.g. RedTrack's `rtkcid`, `clickid`, `cmpid`) that the
 * brand app needs for postback attribution but which `buildHandoffUrl`
 * does not forward (it only handles known click IDs and UTMs).
 *
 * Idempotent: skips params already present on the URL.
 */
const appendExtraTrackingParams = (url: string): string => {
  try {
    const context = readAttributionContext();
    const extraParams = context?.lastTouch?.platformDetails?.extraParams;
    if (!extraParams || Object.keys(extraParams).length === 0) return url;

    const parsed = new URL(url);
    for (const [key, value] of Object.entries(extraParams)) {
      if (value && !parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, value);
      }
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * Build the redirect handler the funnel calls on completion. Resolves the
 * paywall target in priority order:
 *
 *   1. (DEV only) Build-time env var `VITE_BRAND_PAYWALL_URL`. Only honoured
 *      in Vite's dev mode so an engineer can point the funnel at a localhost
 *      brand app (e.g. `http://localhost:5174/`). Never used in production
 *      builds to avoid accidental localhost redirects.
 *   2. Per-funnel handoff URL from answers (see handoff step). When
 *      configured, it overrides the application-level URL.
 *   3. The runtime application context (resolved by /api/application/me).
 *      This is the multi-tenant production path: each application points
 *      at its own paywall_base_url.
 *   4. SpicyChat default — last-resort safety net.
 *
 * The final URL carries `au_h=1` only when the funnel includes an auth
 * step (`account_create` or `user_sync`) so the brand app attempts
 * silent SSO on mount. Funnels without auth steps skip `au_h` to avoid
 * a failed login attempt on arrival. Attribution (`ad_h=<envelope>`) is
 * always appended when an envelope has been captured. Attribution signing
 * failure must never strand the user; we always redirect, even if
 * buildOutboundHandoffUrl rejects (Bugbot NX-9280).
 *
 * **Clerk flow**: When the answers bag contains `__clerk_signin_token`
 * (set by the Clerk path of `user_sync`), the handoff uses `__clerk_external_signin_url`
 * as the base and appends `?token=<signin_token>` instead of `au_h=1`.
 * The ggpt app's `/external/signin` page consumes the token to create a
 * Clerk session and redirects to post-auth.
 */
const resolveTarget = (
  applicationContext: ApplicationContextState,
  answers?: Record<string, unknown>,
): string => {
  // DEV-only override — never applies in production builds.
  if (import.meta.env.DEV) {
    const envOverride = import.meta.env.VITE_BRAND_PAYWALL_URL;
    if (typeof envOverride === "string" && envOverride !== "") {
      return envOverride;
    }
  }
  // Per-funnel handoff URL from a configurable handoff step.
  const handoffUrl = answers?.__handoff_url;
  if (typeof handoffUrl === "string" && handoffUrl !== "") {
    return handoffUrl;
  }
  if (applicationContext.status === "ready") {
    return applicationContext.application.paywallBaseUrl;
  }
  return DEFAULT_PAYWALL_URL;
};

/**
 * Override `redirect_url` on the token-signin URL with the per-funnel
 * handoff destination (`__handoff_url`) so the user lands on the correct
 * page (e.g. a character chat page) after sign-in. Only applies when
 * `__handoff_url` is explicitly set in the answer bag — does NOT fall
 * back to `paywallBaseUrl` to avoid unintentionally overriding the
 * `redirect_url` that was already in the `external_signin_url`.
 *
 * Sets `lock_redirect=false` by default unless the answer bag contains
 * `__lock_redirect` set to `"true"`.
 */
const applyRedirectOverride = (url: URL, answers: Record<string, unknown> | undefined): void => {
  const handoffUrl = answers?.__handoff_url;
  if (typeof handoffUrl !== "string" || handoffUrl === "") return;

  try {
    const targetUrl = new URL(handoffUrl);
    const redirectPath = targetUrl.pathname + targetUrl.search;
    if (redirectPath && redirectPath !== "/") {
      url.searchParams.set("redirect_url", redirectPath);
      const lock = answers?.__lock_redirect === "true" ? "true" : "false";
      url.searchParams.set("lock_redirect", lock);
    }
  } catch {
    // handoffUrl is not a valid URL — leave redirect_url as-is
  }
};

/**
 * Build the Clerk-specific handoff URL when a sign-in token is available.
 * Uses `__clerk_external_signin_url` as the base (e.g.
 * `https://gptgirlfriend.com/external/token-signin`) and appends the
 * sign-in `token` param. The attribution envelope (`ad_h`) is included
 * via `buildOutboundHandoffUrl` so `lastTouch.sessionId` and other
 * attribution data survive the cross-domain redirect.
 *
 * When a per-funnel handoff URL is configured (`__handoff_url`), the
 * `redirect_url` param on the token-signin page is overridden so the
 * user lands on that page (e.g. a character chat page) after sign-in
 * instead of the default `/external/post-auth`.
 *
 * Falls back to `__handoff_url` or paywall URL with the token appended
 * if no explicit signin URL is set.
 */
const buildClerkHandoffUrl = async (
  signinToken: string,
  answers: Record<string, unknown> | undefined,
  applicationContext: ApplicationContextState,
): Promise<string> => {
  const signinUrl = answers?.[CLERK_EXTERNAL_SIGNIN_URL_KEY];
  const base =
    typeof signinUrl === "string" && signinUrl !== ""
      ? signinUrl
      : resolveTarget(applicationContext, answers);

  const withAttribution = await buildOutboundHandoffUrl(base);
  const parsed = new URL(withAttribution ?? base);
  parsed.searchParams.set("token", signinToken);

  applyRedirectOverride(parsed, answers);
  parsed.searchParams.set("ignore_cookie_consent", "true");
  parsed.searchParams.set("ignore_chat_settings", "true");
  return parsed.toString();
};

export const useHandoffRedirect = (
  applicationContext: ApplicationContextState,
  onBeforeRedirect: () => void,
  answers?: Record<string, unknown>,
  steps?: ReadonlyArray<StepRef>,
) => {
  const answersRef = useRef(answers);
  answersRef.current = answers;

  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  return useCallback((): void => {
    onBeforeRedirect();

    const currentAnswers = answersRef.current;
    const clerkToken = currentAnswers?.[CLERK_SIGNIN_TOKEN_ANSWER_KEY];

    if (typeof clerkToken === "string" && clerkToken !== "") {
      buildClerkHandoffUrl(clerkToken, currentAnswers, applicationContext)
        .then((url) => {
          flushEventQueueSync();
          window.location.assign(appendExtraTrackingParams(appendAffiliateRef(url)));
        })
        .catch(() => {
          const signinUrl = currentAnswers?.[CLERK_EXTERNAL_SIGNIN_URL_KEY];
          let base: string;
          try {
            base =
              typeof signinUrl === "string" && signinUrl !== ""
                ? new URL(signinUrl).toString()
                : resolveTarget(applicationContext, currentAnswers);
          } catch {
            base = resolveTarget(applicationContext, currentAnswers);
          }
          const parsed = new URL(base);
          parsed.searchParams.set("token", clerkToken);
          applyRedirectOverride(parsed, currentAnswers);
          parsed.searchParams.set("ignore_cookie_consent", "true");
          parsed.searchParams.set("ignore_chat_settings", "true");
          flushEventQueueSync();
          window.location.assign(appendExtraTrackingParams(appendAffiliateRef(parsed.toString())));
        });
      return;
    }

    const target = resolveTarget(applicationContext, currentAnswers);
    const shouldAppendAuth = stepsRef.current ? hasAuthStep(stepsRef.current) : true;
    const maybeAppendAuth = shouldAppendAuth ? appendAuthHandoffSignal : (url: string) => url;

    buildOutboundHandoffUrl(target)
      .then((handoffUrl) => {
        flushEventQueueSync();
        window.location.assign(
          appendExtraTrackingParams(appendAffiliateRef(maybeAppendAuth(handoffUrl ?? target))),
        );
      })
      .catch(() => {
        flushEventQueueSync();
        window.location.assign(
          appendExtraTrackingParams(appendAffiliateRef(maybeAppendAuth(target))),
        );
      });
  }, [applicationContext, onBeforeRedirect]);
};
